'use client';

import { motion, useMotionValueEvent, useScroll } from 'motion/react';
import { useRef, useState } from 'react';

import { Container } from '@/components/container';
import { Blink, Brackets, ThumbSmall } from '@/components/icons';
import { SectionDescription } from '@/components/section-description';

const QUOTE_1 = `“Security was a key focus for us since the first day of our company. We constantly practice, we indulge in threat modelling, we try to consider implications as early as possible within the SDLC, before any code is written.`;
const QUOTE_2 = `We try to be this 1% better each week, both ourselves and our software, as one is the consequence of the other.”`;

const SWING_PRIMARY_BLACK = '#313131';
const SWING_PRIMARY_WHITE = '#F1F1F1';

export const SecuritySection = () => {
    const ref = useRef(null);
    const [progress, setProgress] = useState(0);

    const { scrollYProgress } = useScroll({
        offset: ['start end', 'start start'],
        target: ref,
    });

    useMotionValueEvent(scrollYProgress, 'change', () => {
        const offsetStart = 0.4;
        const offsetEnd = 0.2;
        const value = Math.min(
            1,
            Number(Math.max(0, scrollYProgress.get() - offsetStart)) /
                (1 - offsetStart - offsetEnd),
        );

        setProgress(value);
    });

    return (
        <Container
            verticalSpacing
            as="section"
            className="max-sm:px-0"
        >
            <SectionDescription
                title="A few words about security"
                variant="top"
                className="mb-8"
            />
            <div
                className={`
                  relative overflow-hidden rounded-3xl bg-primary-green
                  bg-thumb-small bg-no-repeat px-6 py-20 text-primary-green
                  transition-all duration-200

                  [background-position-y:6rem]

                  lg:rounded-none lg:bg-thumb-large lg:px-20
                  lg:[background-position-y:center] lg:hover:rounded-[60px]
                `}
            >
                <h2
                    className={`
                      typography-h4 mb-10 text-center font-bold
                      text-primary-black

                      lg:typography-h3
                    `}
                >
                    Security matters
                </h2>

                <div
                    className={`
                      text-center text-2xl font-bold

                      lg:mx-28 lg:text-left lg:text-4xl
                    `}
                    ref={ref}
                >
                    <div className={`pb-8`}>
                        {QUOTE_1?.split('').map(
                            (char: string, index: number) => (
                                <motion.span
                                    key={index}
                                    initial={{ color: '#47B749' }}
                                    animate={{
                                        color:
                                            progress >
                                            index /
                                                (QUOTE_1.length +
                                                    QUOTE_2.length)
                                                ? SWING_PRIMARY_WHITE
                                                : SWING_PRIMARY_BLACK,
                                    }}
                                    transition={{ duration: 0.1 }}
                                    className="-mx-px"
                                >
                                    {char}
                                </motion.span>
                            ),
                        )}
                    </div>
                    <div>
                        {QUOTE_2?.split('').map(
                            (char: string, index: number) => (
                                <motion.span
                                    key={index + QUOTE_1.length}
                                    initial={{ color: '#47B749' }}
                                    animate={{
                                        color:
                                            progress >
                                            (index + QUOTE_1.length) /
                                                (QUOTE_1.length +
                                                    QUOTE_2.length)
                                                ? SWING_PRIMARY_WHITE
                                                : SWING_PRIMARY_BLACK,
                                    }}
                                    transition={{ duration: 0.1 }}
                                    className="-mx-px"
                                >
                                    {char}
                                </motion.span>
                            ),
                        )}
                    </div>
                </div>

                <div
                    className={`mt-10 text-center font-bold text-primary-black`}
                >
                    <p
                        className={`
                          text-lg

                          lg:text-base
                        `}
                    >
                        Tomasz Kopczuk
                    </p>
                    <p
                        className={`
                          text-xs

                          lg:text-sm
                        `}
                    >
                        Director of Security & Technology, Hippo
                    </p>
                    <p
                        className={`
                          text-xs

                          lg:text-sm
                        `}
                    >
                        Co-founder, SwingDev
                    </p>
                </div>

                <Brackets
                    className={`
                      absolute left-8 top-8 -translate-x-1/2 -translate-y-1/2
                      text-primary-black

                      lg:left-12 lg:top-12
                    `}
                />
                <Brackets
                    className={`
                      absolute bottom-8 right-8 translate-x-1/2 translate-y-1/2
                      text-primary-black

                      lg:bottom-12 lg:right-12
                    `}
                />
                <Blink
                    className={`
                      absolute right-8 top-8 -translate-y-1/2 translate-x-1/2

                      lg:right-12 lg:top-12
                    `}
                />
                <Blink
                    className={`
                      absolute bottom-8 left-8 -translate-x-1/2 translate-y-1/2

                      lg:bottom-12 lg:left-12
                    `}
                />
                <ThumbSmall
                    className={`
                      absolute -right-8 bottom-14

                      lg:bottom-24 lg:right-24
                    `}
                />
            </div>
        </Container>
    );
};

// const gradientAnimationClass = "animate-text-gradient bg-gradient-to-r from-primary-green to-primary-black bg-[200%_auto] bg-clip-text text-transparent"
